<template>
    <div>
        <div class="prose prose-purple">
            <h1>
                Grass Textures
            </h1>
            <p>
                Using 2D grass textures on your terrain is not supported by Nature Renderer. This article explains why,
                and how
                to convert grass textures to detail meshes and set them up correctly.
            </p>
            <hr>
            <h2>About grass textures</h2>
            <p>
                Unity terrain supports using a simple 2D texture as grass. A single flat quad mesh will be created
                internally
                for the texture and can be painted on the terrain just like any detail mesh.
            </p>
            <figure>
                <img
                    :src="GrassTextureExampleImg"
                    class="mx-auto"
                >
                <figcaption>Example grass texture in the default Unity renderer</figcaption>
            </figure>
            <p>
                While this works fine for the default Unity renderer, it is not something that is supported by Nature
                Renderer. The main reason for this is that Nature Renderer loads the render settings from the
                <InlineLink href="object-settings#object-settings">
                    Mesh
                    Renderer
                </InlineLink>
                and
                <InlineLink href="object-settings">
                    Object Settings
                </InlineLink>
                components on the grass prefab. Since grass textures are not prefabs they
                can't have any settings.
            </p>
            <p>
                To easily solve this, Nature Renderer provides a way to automatically create a prefab for each grass
                texture.
            </p>
            <h2>Convert grass textures</h2>
            <p>
                In the Vegetation section of Nature Renderer you will see an error message for each detail object that
                is
                using a grass texture instead of a prefab.
            </p>
            <figure>
                <img
                    :src="GrassTextureImg"
                    class="mx-auto"
                >
                <figcaption>Error message for grass textures</figcaption>
            </figure>
            <p>
                To convert a texture to a prefab, select the grass texture by
                clicking on the item in the list. Then, click "Create New" to automatically create a new prefab and
                material or assign an existing prefab if you already have one in your project.
            </p>
            <figure>
                <img
                    :src="GrassTextureConvertedImg"
                    class="mx-auto"
                >
                <figcaption>Grass texture after being converted to a prefab</figcaption>
            </figure>
            <p>
                Render settings such as shadows, render distance, and density can now be edited on the
                <InlineLink href="object-settings#object-settings">
                    Mesh Renderer
                </InlineLink>
                and
                <InlineLink href="object-settings">
                    Object Settings
                </InlineLink>
                components on the newly created prefab.
            </p>
            <h2>Performance implications</h2>
            <p>
                Single quad meshes are slow to render and do not result in optimal performance. Nature Renderer relies
                on
                a rendering technique called GPU instancing, this technique is very efficient at rendering large amounts
                of the same object but it comes with a few performance drawbacks. One of them is that meshes with low
                vertex/triangle counts are not very efficient to render.
            </p>
            <p>
                It is best to have meshes with more than 256 vertices in order to make the best use of the GPU's
                resources. Single quad meshes (which are used for grass textures) have only 2 triangles and result in
                very poor distribution of the GPU's resources.
            </p>
            <figure>
                <img
                    :src="SingleQuadAndPatchImg"
                    class="mx-auto"
                >
                <figcaption>Left: Single quad mesh (slow). Right: Larger grass mesh (fast).</figcaption>
            </figure>
            <p>
                Additionally, Nature Renderer needs to compute the LODs and culling for each grass instance. More
                instances means the render pipeline has to do more computations for the LODs and uses more memory to
                store the results, resulting in suboptimal performance.
            </p>
            <p>
                Single quad meshes result in a lot of instances that need to be placed in order to achieve a dense grass
                field, slowing down the rendering pipeline. The recommended approach is to avoid grass textures and
                small meshes when possible and use meshes
                of
                larger grass patches instead.
            </p>
        </div>
    </div>
</template>

<script lang="ts" setup>
import GrassTextureExampleImg from "../../../assets/nature-renderer/nature-renderer-doc-grass-texture.jpg";
import GrassTextureImg from "../../../assets/nature-renderer/nature-renderer-doc-convert-grass-texture.jpg";
import GrassTextureConvertedImg from "../../../assets/nature-renderer/nature-renderer-doc-grass-texture-converted.jpg";
import SingleQuadAndPatchImg from "../../../assets/nature-renderer/nature-renderer-doc-single-quad-vs-patch.jpg";
import InlineLink from "../../../components/InlineLink.vue";
import { useMeta } from "vue-meta";

const article = {
    image: "",
    section: "Features",
    title: "Grass Textures",
    meta: {
        author: "",
        avatar: "",
        postedDate: ""
    }
};

useMeta({
    title: article.title + " | Nature Renderer"
});

</script>
